import { type VFC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { LoadingSpinner } from "widgets";

import type { User } from "../../graphql.js";
import { type IDashboardCardProjectsList, loadDashboardCardProjectsList } from "../../redux/modules/dashboard.js";
import { getCreateNewProjectButtonProps } from "../../redux/modules/project-list.js";
import type { IAppState } from "../../redux/reducer.js";
import type { ReduxDispatch } from "../../redux/redux-utils.js";
import GenericCard, { type IGenericCardProps } from "../core/GenericCard.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import CreateProjectDialog from "../projects/CreateProjectDialog.js";
import ProjectsDashboardCardEditingForm from "./ProjectsDashboardCardEditingForm.js";
import { ProjectTileMapper } from "./TilesMappers.js";

export interface IProjectsListCardConfig {
  sorting: string;
  includeProjects: number[];
}

const ProjectsDashboardCard2: VFC<IGenericCardProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();

  const [showNewProjectDialog, setShowNewProjectDialog] = useState<boolean>(false);

  const projectList = useSelector<IAppState, IDashboardCardProjectsList>(
    (state) => state.dashboardState.projectsListCards[props.id],
  );
  const user = useSelector<IAppState, User>((state) => state.auth.session);

  const loadProjectList = useCallback(() => {
    return dispatch(
      loadDashboardCardProjectsList(props.id, props.config && (props.config.specificConfig as IProjectsListCardConfig)),
    );
  }, [dispatch, props.id, props.config]);

  useEffect(() => {
    void loadProjectList();
  }, []);

  return (
    <GenericCard
      {...props}
      mainAction={getCreateNewProjectButtonProps(user, () => setShowNewProjectDialog(true))}
      refreshData={loadProjectList}
      editingForm={ProjectsDashboardCardEditingForm}
      goToAction={() => navigate(`/projects`)}
      tipTourId={"home-page-projects-title"}
      footerContent={
        <span>
          View <a onClick={() => navigate(`/projects`)}>all projects</a>.
        </span>
      }
    >
      {projectList && projectList.isLoading && <LoadingSpinner inCenter />}

      {projectList && !projectList.isLoading && projectList.projects && (
        <div className="tiles_grid tiles_grid--projects">
          {projectList.projects.map((project) => (
            <ProjectTileMapper key={project.id} data={project} />
          ))}
        </div>
      )}

      {showNewProjectDialog && (
        <ModalControlledOverlay close={() => setShowNewProjectDialog(false)} isOpen={true}>
          {({ close }) => <CreateProjectDialog onApply={close} onCancel={close} />}
        </ModalControlledOverlay>
      )}
    </GenericCard>
  );
};

export { ProjectsDashboardCard2 };
